import React, { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import SafeHtml from "../../components/safe-html";
import DuvidaFrequenteService from "../../services/duvida-frequente.service";
import {
  DuvidaFrequente,
  DuvidaFrequenteParams,
} from "../../shared/@types/duvidaFrequente";
import { Mensagem } from "../../shared/mensagem/Mensagem";
import ToastService from "../../../vendors/services/toastService";
import "./duvidas-frequentes.scss";

interface PaginationState {
  first: number;
  rows: number;
  page: number;
}

const DEBOUNCE_DELAY = 400;

function ordenarDuvidas(
  duvidas: DuvidaFrequente[]
): DuvidaFrequente[] {
  const ordem = (duvida: DuvidaFrequente) => {
    const valor = Number(duvida.nuOrdemSuporte);
    return Number.isFinite(valor) ? valor : Number.MAX_SAFE_INTEGER;
  };

  return [...duvidas].sort(
    (a, b) =>
      ordem(a) - ordem(b) ||
      a.cdSeqDuvidaFrequente - b.cdSeqDuvidaFrequente
  );
}

export default function DuvidasFrequentes() {
  const service = useMemo(() => new DuvidaFrequenteService(), []);
  const [termo, setTermo] = useState("");
  const [termoConsultado, setTermoConsultado] = useState("");
  const [duvidas, setDuvidas] = useState<DuvidaFrequente[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    first: 0,
    rows: 10,
    page: 0,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPagination((atual) => ({ ...atual, first: 0, page: 0 }));
      setTermoConsultado(termo.trim());
    }, DEBOUNCE_DELAY);

    return () => window.clearTimeout(timeout);
  }, [termo]);

  useEffect(() => {
    let active = true;
    const params: DuvidaFrequenteParams = {
      page: pagination.page,
      pageSize: pagination.rows,
    };

    if (termoConsultado) {
      params.termo = termoConsultado;
    }

    setLoading(true);
    setError(false);

    service
      .consultar(params)
      .then((response) => {
        if (!active) {
          return;
        }

        const result = response.result;
        const items = Array.isArray(result?.items) ? result!.items : [];
        const duvidasAtivas = items.filter((duvida) => duvida.stAtivo);

        setDuvidas(ordenarDuvidas(duvidasAtivas));
        setTotalItems(result?.totalItems ?? 0);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setDuvidas([]);
        setTotalItems(0);
        setError(true);
        ToastService.showError(Mensagem.FALHA_REQUISICAO);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [pagination.page, pagination.rows, service, termoConsultado]);

  const limparPesquisa = () => {
    setTermo("");
    setTermoConsultado("");
    setPagination((atual) => ({ ...atual, first: 0, page: 0 }));
  };

  return (
    <Card className="duvidas-frequentes">
      <h2 className="duvidas-frequentes-titulo">Dúvidas frequentes</h2>
      <div className="duvidas-frequentes-pesquisa">
        <span className="p-input-icon-left duvidas-frequentes-campo">
          <i className="pi pi-search" aria-hidden="true" />
          <InputText
            aria-label="Pesquisar dúvidas frequentes"
            maxLength={200}
            placeholder="Pesquisar dúvidas frequentes..."
            value={termo}
            onChange={(event) => setTermo(event.target.value)}
          />
        </span>
        <Button
          aria-label="Limpar pesquisa"
          className="p-button-outlined duvidas-frequentes-limpar"
          disabled={!termo}
          icon="pi pi-times"
          label="Limpar"
          type="button"
          onClick={limparPesquisa}
        />
      </div>

      {loading && (
        <div
          className="duvidas-frequentes-estado"
          aria-live="polite"
          role="status"
        >
          <ProgressSpinner
            style={{ width: "40px", height: "40px" }}
            strokeWidth="4"
          />
          <span>Carregando dúvidas frequentes...</span>
        </div>
      )}

      {!loading && error && (
        <div className="duvidas-frequentes-estado" role="alert">
          Não foi possível carregar as dúvidas frequentes no momento.
        </div>
      )}

      {!loading && !error && duvidas.length === 0 && (
        <div className="duvidas-frequentes-estado">
          Nenhuma dúvida frequente encontrada
        </div>
      )}

      {!loading && !error && duvidas.length > 0 && (
        <>
          <Accordion className="duvidas-frequentes-accordion">
            {duvidas.map((duvida) => (
              <AccordionTab
                key={duvida.cdSeqDuvidaFrequente}
                header={duvida.noDuvidaFrequente}
              >
                <SafeHtml
                  className="duvidas-frequentes-resposta"
                  html={duvida.dsDuvidaFrequente}
                />
              </AccordionTab>
            ))}
          </Accordion>

          <div className="duvidas-frequentes-paginacao">
            <Paginator
              first={pagination.first}
              rows={pagination.rows}
              totalRecords={totalItems}
              rowsPerPageOptions={[10, 20, 30, 40, 50]}
              onPageChange={(event) =>
                setPagination({
                  first: event.first,
                  rows: event.rows,
                  page: event.page,
                })
              }
            />
          </div>
        </>
      )}
    </Card>
  );
}
